import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card } from 'reactstrap';
import { Button } from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyIcon from '@mui/icons-material/Key';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';

import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

const LICENSE_COLUMNS = [
  {
    key: 'customerName',
    header: 'Customer Name',
    sortable: true,
  },
  {
    key: 'licenceNo',
    header: 'License No.',
    sortable: true,
  },
  {
    key: 'lastLoggedRiderUserName',
    header: 'Last Logged Rider',
    sortable: true,
    render: (value) => value || '—'
  },
   {
    key: 'lastLoggedRiderName',
    header: 'Rider ID',
    sortable: true,
    render: (value) => value || '—'
  },
];

const MobileDeviceKeyDetails = ({
  reactTableData,
  isNoRecordFound,
  canCancelLicense,
  onCancelLicense
}) => {
  const rows = reactTableData?.tableRowsData ?? [];

  const [selectedDevice, setSelectedDevice] = useState(
    rows.length > 0 ? rows[0] : null
  );

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>
          {!isNoRecordFound ? (
            <RadioSelectTable
              rows={rows}
              columns={LICENSE_COLUMNS}
              rowKey="devId"
              selectedKey={selectedDevice?.devId}
              onSelect={setSelectedDevice}
              searchKeys={['customerName', 'licenceNo', 'lastLoggedRiderUserName', 'lastLoggedRiderName']}
              searchPlaceholder="Search by Customer, License No, Rider Name..."
              sortKey="customerName"
              headerTitle="Device Licenses"
              pageSizes={[5, 10, 20, 50]}
            />
          ) : (
            <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
              <p className="text-muted">No licenses found.</p>
            </Card>
          )}
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedDevice ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <SmartphoneIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedDevice.customerName}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)', display: 'block', wordBreak: 'break-all' }}>
                          License: {selectedDevice.licenceNo}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* License Information */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        License Info
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Customer Name:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <AccountCircleIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDevice.customerName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">License No:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1" style={{ wordBreak: 'break-all', textAlign: 'right' }}>
                            <KeyIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDevice.licenceNo || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Logged Rider Details */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Last Logged Rider Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Rider Name:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <PersonPinIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDevice.lastLoggedRiderUserName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Rider ID:</span>
                          <span className="fw-bold small text-dark">
                            {selectedDevice.lastLoggedRiderName || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Block */}
                    {canCancelLicense && (
                      <div style={{ marginTop: 'auto' }}>
                        <Button
                          variant="contained"
                          color="error"
                          size="medium"
                          startIcon={<CancelScheduleSendIcon />}
                          onClick={() => onCancelLicense(selectedDevice.devId)}
                          style={{
                            width: '100%',
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                          }}
                        >
                          Cancel Device License
                        </Button>
                      </div>
                    )}

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>🔑</div>
                <h5 className="fw-bold text-muted">No License Selected</h5>
                <p className="text-muted small">Select a license from the list to view specifications.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

MobileDeviceKeyDetails.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  canCancelLicense: PropTypes.bool,
  onCancelLicense: PropTypes.func.isRequired,
};

export default MobileDeviceKeyDetails;
